// components/modals/SpendingDetailsModal.jsx - COMBINED LAYOUT WITH TRANSACTION HISTORY
import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Card,
  Stack,
  Avatar,
  Chip,
  Button,
  useMediaQuery,
  LinearProgress,
  Divider,
  Alert,
  IconButton,
  Paper,
  Fade,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useTheme, alpha } from '@mui/material/styles';
import {
  Group as GroupIcon,
  Business as BusinessIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCard as CreditCardIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  AttachMoney as AttachMoneyIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Receipt as ReceiptIcon,
  RemoveCircle as RemoveCircleIcon,
  AddCircle as AddCircleIcon,
  SwapHoriz as SwapHorizIcon,
  Description as DescriptionIcon,
  HelpOutline as HelpOutlineIcon,
  Person as PersonIcon,
  SupervisorAccount as SupervisorAccountIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import client from "../../../API";

const SpendingDetailsModal = ({ open, onClose, spendingInfo }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [rightPanelTab, setRightPanelTab] = useState(0); // 0=history, 1=teams
  const [historyFilter, setHistoryFilter] = useState(0); // 0=all, 1=team, 2=company

  // Fetch spending power history
  const {
    data: historyData,
    isLoading: historyLoading,
    error: historyError,
    refetch: refetchHistory
  } = useQuery(
    ['spendingPowerHistory'],
    () => client.spendingPowerLedger.getHistory(),
    {
      enabled: open && rightPanelTab === 0,
      staleTime: 30000,
      refetchOnWindowFocus: false,
    }
  );

  // Refetch when switching to history tab
  useEffect(() => {
    if (open && rightPanelTab === 0) {
      refetchHistory();
    }
  }, [open, rightPanelTab, refetchHistory]);

  if (!spendingInfo) return null;

  const isTeamSource = spendingInfo.spending_source === 'team';
  const hasTeams = spendingInfo.has_team_assignment && spendingInfo.teams?.length > 0;
  
  // Calculate utilization percentage
  const effectiveLimit = parseFloat(spendingInfo.effective_spending_limit || 0);
  const teamUsed = parseFloat(spendingInfo.team_used_spending || 0);
  const teamAvailable = parseFloat(spendingInfo.team_available_spending || 0);
  const utilizationPercent = effectiveLimit > 0 ? (teamUsed / effectiveLimit) * 100 : 0;

  const getUtilizationColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    if (percentage >= 50) return 'info';
    return 'success';
  };

  // Process transaction history
  const transactions = historyData?.transactions || [];

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    if (historyFilter === 0) return transactions;
    if (historyFilter === 1) return transactions.filter(t => t.type === 'team_spending');
    if (historyFilter === 2) return transactions.filter(t => t.type === 'company_spending');
    return transactions;
  }, [transactions, historyFilter]);

  // Transaction display logic
  const getTransactionDisplay = (transaction) => {
    const isUse = transaction.operation_type === 'use';
    const isRelease = transaction.operation_type === 'release';
    const isAllocate = transaction.operation_type === 'allocate';
    const isAdjust = transaction.operation_type === 'adjust';
    
    if (isUse) {
      return {
        icon: <RemoveCircleIcon />,
        color: 'error',
        bgColor: 'error.lighter',
        title: 'Spending Power Used',
        subtitle: transaction.details?.description || transaction.reason || 'BlitzPay transaction',
        tooltip: 'Spending power was reserved for a BlitzPay advance',
      };
    }
    
    if (isRelease) {
      return {
        icon: <AddCircleIcon />,
        color: 'success',
        bgColor: 'success.lighter',
        title: 'Spending Power Released',
        subtitle: transaction.details?.description || transaction.reason || 'Funds returned',
        tooltip: 'Previously reserved spending power was returned and is now available',
      };
    }
    
    if (isAllocate) {
      return {
        icon: <SwapHorizIcon />,
        color: 'info',
        bgColor: 'info.lighter',
        title: 'Allocation Changed',
        subtitle: transaction.details?.description || transaction.reason || 'Spending power allocated',
        tooltip: 'Your spending power allocation was adjusted by an administrator',
      };
    }
    
    if (isAdjust) {
      return {
        icon: <TrendingUpIcon />,
        color: 'warning',
        bgColor: 'warning.lighter',
        title: 'Spending Adjusted',
        subtitle: transaction.details?.description || transaction.reason || 'Rebalancing',
        tooltip: 'Spending power was rebalanced across teams or categories',
      };
    }
    
    return {
      icon: <DescriptionIcon />,
      color: 'text.primary',
      bgColor: 'grey.100',
      title: transaction.operation_type?.replace(/_/g, ' ').toUpperCase() || 'Transaction',
      subtitle: transaction.details?.description || transaction.reason || '',
      tooltip: 'General spending power transaction',
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  // Transaction History Panel Content
  const TransactionHistoryContent = () => {
    if (historyLoading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
            Loading transaction history...
          </Typography>
        </Box>
      );
    }

    if (historyError) {
      return (
        <Alert severity="error" sx={{ my: 4 }}>
          <Typography variant="body2">
            Failed to load transaction history. Please try again.
          </Typography>
        </Alert>
      );
    }

    if (!transactions || transactions.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              backgroundColor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 3,
            }}
          >
            <ReceiptIcon sx={{ fontSize: 60, color: 'grey.400' }} />
          </Box>
          <Typography variant="h5" color="text.primary" gutterBottom sx={{ fontWeight: 700 }}>
            No Transaction History
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, margin: '0 auto' }}>
            Your spending power transactions will appear here once you start using BlitzPay.
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {/* Filter Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={historyFilter} 
            onChange={(e, newValue) => setHistoryFilter(newValue)}
            sx={{
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                px: 2,
              },
            }}
          >
            <Tab label={`All (${transactions.length})`} />
            <Tab label={`Team (${transactions.filter(t => t.type === 'team_spending').length})`} />
            <Tab label={`Company (${transactions.filter(t => t.type === 'company_spending').length})`} />
          </Tabs>
        </Box>

        {/* Transaction List - SIMPLIFIED */}
        <Stack spacing={2}>
          {filteredTransactions.map((transaction, index) => {
            const display = getTransactionDisplay(transaction);
            const amount = Math.abs(parseFloat(transaction.amount || 0));
            const isNegative = transaction.operation_type === 'use';
            
            // Get performer info
            const performer = transaction.performed_by;
            const invoice = transaction.invoice;
            const payout = transaction.payout;
            
            // Get balance info based on transaction type
            let balanceInfo = null;
            if (transaction.type === 'team_spending') {
              balanceInfo = {
                label: 'Team Used After',
                value: transaction.new_used || 0
              };
            } else if (transaction.type === 'company_spending') {
              if (transaction.new_spending_used !== null && transaction.new_spending_used !== undefined) {
                balanceInfo = {
                  label: 'Company Used After',
                  value: transaction.new_spending_used
                };
              }
            }
            
            return (
              <Paper
                key={transaction.id || index}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: `${display.color}.main`,
                    boxShadow: 2,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                {/* Main Transaction Row */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Tooltip title={display.tooltip} arrow placement="top">
                    <Avatar 
                      sx={{ 
                        bgcolor: display.bgColor,
                        color: `${display.color}.main`,
                        width: 48,
                        height: 48,
                      }}
                    >
                      {display.icon}
                    </Avatar>
                  </Tooltip>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.25 }}>
                          {display.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {display.subtitle}
                        </Typography>
                      </Box>
                      
                      <Tooltip 
                        title={isNegative ? "Amount deducted from your spending power" : "Amount added to your spending power"} 
                        arrow 
                        placement="left"
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 800,
                            color: isNegative ? 'error.main' : 'success.main',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {isNegative ? '-' : '+'}${amount.toFixed(2)}
                        </Typography>
                      </Tooltip>
                    </Box>
                    
                    {/* Metadata Row - Better organized */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1.5 }}>
                      {/* Performer */}
                      {performer && (
                        <Tooltip 
                          title={performer.supervisor ? "This transaction was performed by a supervisor" : "User who performed this transaction"} 
                          arrow
                        >
                          <Chip
                            icon={performer.supervisor ? <SupervisorAccountIcon sx={{ fontSize: 14 }} /> : <PersonIcon sx={{ fontSize: 14 }} />}
                            label={`${performer.first_name} ${performer.last_name}`}
                            size="small"
                            variant="outlined"
                            color={performer.supervisor ? "primary" : "default"}
                            sx={{
                              height: 24,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                            }}
                          />
                        </Tooltip>
                      )}
                      
                      {/* Team/Company Badge */}
                      {transaction.team_name ? (
                        <Tooltip title="This transaction affected team-allocated spending power" arrow>
                          <Chip 
                            icon={<GroupIcon sx={{ fontSize: 14 }} />}
                            label={transaction.team_name}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ 
                              height: 24, 
                              fontSize: '0.7rem',
                              fontWeight: 600,
                            }}
                          />
                        </Tooltip>
                      ) : transaction.type === 'company_spending' ? (
                        <Tooltip title="This transaction affected company-wide spending power" arrow>
                          <Chip 
                            icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                            label="Company Direct"
                            size="small"
                            variant="outlined"
                            color="success"
                            sx={{ 
                              height: 24, 
                              fontSize: '0.7rem',
                              fontWeight: 600,
                            }}
                          />
                        </Tooltip>
                      ) : null}
                      
                      {/* PO Number */}
                      {transaction.po_number && (
                        <Tooltip title="Purchase order number associated with this transaction" arrow>
                          <Chip 
                            label={`PO: ${transaction.po_number}`}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              height: 24, 
                              fontSize: '0.7rem',
                            }}
                          />
                        </Tooltip>
                      )}
                      
                      {/* Date */}
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem', ml: 'auto' }}
                      >
                        {formatDate(transaction.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Extended Details - Cleaner layout */}
                {(balanceInfo || invoice || payout) && (
                  <Box 
                    sx={{ 
                      pt: 2,
                      mt: 2,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack spacing={2}>
                      {/* Balance Info */}
                      {balanceInfo && (
                        <Tooltip title="Your total used spending power after this transaction" arrow placement="right">
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {balanceInfo.label}
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                              ${balanceInfo.value.toFixed(2)}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                      
                      {/* Invoice Info */}
                      {invoice && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                            Related Invoice
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip 
                              label={`Invoice #${invoice.id}`}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', fontWeight: 600, height: 24 }}
                            />
                            <Chip 
                              label={invoice.status}
                              size="small"
                              color={invoice.status === 'paid' ? 'success' : invoice.status === 'approved' ? 'info' : 'warning'}
                              sx={{ fontSize: '0.7rem', textTransform: 'capitalize', height: 24 }}
                            />
                            {invoice.client_name && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {invoice.client_name}
                              </Typography>
                            )}
                            {invoice.amount_due && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                ${parseFloat(invoice.amount_due).toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      
                      {/* Payout Info */}
                      {payout && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                            Related Payout
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip 
                              label={payout.role || 'Creator'}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', fontWeight: 600, height: 24 }}
                            />
                            <Chip 
                              label={payout.status}
                              size="small"
                              color={payout.status === 'paid' ? 'success' : payout.status === 'approved' ? 'info' : 'warning'}
                              sx={{ fontSize: '0.7rem', textTransform: 'capitalize', height: 24 }}
                            />
                            {payout.blitzpay && (
                              <Chip 
                                label={`BlitzPay ${payout.interest_rate}%`}
                                size="small"
                                color="primary"
                                sx={{ fontSize: '0.7rem', height: 24 }}
                              />
                            )}
                            {payout.amount && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                ${parseFloat(payout.amount).toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      </Box>
    );
  };

  // Team Memberships Panel Content
  const TeamMembershipsContent = () => {
    if (!hasTeams) {
      return (
        <Box sx={{ textAlign: 'center', py: 12 }}>
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              backgroundColor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 3,
            }}
          >
            <GroupIcon sx={{ fontSize: 60, color: 'grey.400' }} />
          </Box>
          <Typography variant="h5" color="text.primary" gutterBottom sx={{ fontWeight: 700 }}>
            No Team Memberships
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, margin: '0 auto', mb: 4 }}>
            You're not currently part of any teams. Contact your administrator to request team access.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<SettingsIcon />}
            onClick={() => window.location.href = '/user'}
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
            }}
          >
            Manage Teams
          </Button>
        </Box>
      );
    }

    return (
      <Stack spacing={3}>
        {spendingInfo.teams.map(team => {
          const teamAllocated = parseFloat(team.allocated_spending_power || 0);
          const teamUsedAmount = parseFloat(team.used_spending_power || 0);
          const teamUtilization = teamAllocated > 0 ? (teamUsedAmount / teamAllocated) * 100 : 0;
          const isPrimary = team.id === spendingInfo.primary_team_id;

          return (
            <Paper 
              key={team.id} 
              sx={{ 
                p: 3, 
                borderRadius: 2.5,
                border: '2px solid',
                borderColor: isPrimary ? 'primary.main' : 'divider',
                bgcolor: isPrimary ? alpha(theme.palette.primary.main, 0.02) : 'background.paper',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 3 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: isPrimary ? 'primary.main' : alpha(theme.palette.primary.main, 0.1), 
                    color: isPrimary ? 'white' : 'primary.main',
                    width: 56, 
                    height: 56 
                  }}
                >
                  <GroupIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="h6" fontWeight={700}>
                      {team.name}
                    </Typography>
                    {isPrimary && (
                      <Tooltip title="This is your primary team - transactions default to this team's spending power" arrow>
                        <Chip 
                          label="Primary" 
                          size="small" 
                          color="primary"
                          sx={{ height: 24, fontSize: '0.75rem', fontWeight: 700 }}
                        />
                      </Tooltip>
                    )}
                    <Chip 
                      label={team.spending_power_status || 'active'} 
                      size="small"
                      color={team.spending_power_status === 'active' ? 'success' : 'warning'}
                      sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {teamUtilization.toFixed(1)}% of allocation used
                  </Typography>
                </Box>
                <Tooltip title="Total spending power allocated to this team" arrow placement="left">
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h4" fontWeight={800} color="primary.main">
                      ${teamAllocated.toFixed(0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Allocated
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Tooltip title="Percentage of allocated spending power currently in use" arrow>
                    <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ cursor: 'help' }}>
                      Utilization
                    </Typography>
                  </Tooltip>
                  <Typography variant="body2" fontWeight={700}>
                    {teamUtilization.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(teamUtilization, 100)}
                  color={getUtilizationColor(teamUtilization)}
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    backgroundColor: alpha(theme.palette.grey[300], 0.3)
                  }}
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Tooltip title="Amount of spending power currently reserved for active BlitzPay advances" arrow>
                    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, textAlign: 'center', cursor: 'help' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Used
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="warning.main">
                        ${teamUsedAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={6}>
                  <Tooltip title="Amount of spending power available for new BlitzPay advances" arrow>
                    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, textAlign: 'center', cursor: 'help' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Remaining
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="success.main">
                        ${(teamAllocated - teamUsedAmount).toFixed(2)}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              </Grid>
            </Paper>
          );
        })}
      </Stack>
    );
  };

  // Mobile View - Dialog
  if (isMobile) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ 
          elevation: 0,
          sx: { 
            borderRadius: isMobile ? 0 : 3,
            boxShadow: isMobile ? 'none' : theme.shadows[24],
            m: isMobile ? 0 : 2,
            ...(isMobile && {
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              height: '100vh',
              maxHeight: '100vh',
            }),
            ...(!isMobile && {
              maxHeight: '90vh',
            }),
          } 
        }}
      >
        {/* Header */}
        <DialogTitle 
          sx={{ 
            pb: 1,
            ...(isMobile && {
              pt: 'max(16px, env(safe-area-inset-top, 0px))',
            }),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: isMobile ? 40 : 48,
                height: isMobile ? 40 : 48,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isTeamSource ? (
                <GroupIcon color="primary" sx={{ fontSize: isMobile ? 24 : 28 }} />
              ) : (
                <BusinessIcon color="primary" sx={{ fontSize: isMobile ? 24 : 28 }} />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                fontWeight={700}
              >
                Spending Power Details
              </Typography>
              <Typography 
                variant={isMobile ? "caption" : "body2"} 
                color="text.secondary"
                sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {isTeamSource 
                  ? (spendingInfo.primary_team_name || 'Team Spending') 
                  : (spendingInfo.company_name || 'Company Spending')}
              </Typography>
            </Box>
            {isMobile && (
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  color: 'text.secondary',
                  bgcolor: alpha(theme.palette.action.hover, 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.2) }
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        
        {/* Scrollable Content */}
        <DialogContent 
          sx={{ 
            px: isMobile ? 2 : 3, 
            py: isMobile ? 2 : 3,
            overflowY: 'auto',
            ...(isMobile && {
              flex: 1,
              WebkitOverflowScrolling: 'touch',
              paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            }),
          }}
        >
          <Stack spacing={3}>
            {/* Spending Source Alert */}
            <Alert 
              severity={isTeamSource ? "info" : "success"} 
              icon={<InfoIcon />}
              sx={{ borderRadius: 2 }}
            >
              <Typography 
                variant={isMobile ? "caption" : "body2"} 
                fontWeight={600}
              >
                {isTeamSource 
                  ? `You're using team-allocated spending power from ${spendingInfo.primary_team_name}`
                  : `You're using company-wide spending power`}
              </Typography>
            </Alert>

            {/* Primary Spending Power Card */}
            <Card 
              elevation={0}
              sx={{ 
                p: isMobile ? 2.5 : 3, 
                borderRadius: 3,
                background: `linear-gradient(135deg, 
                  ${alpha(theme.palette.primary.main, 0.1)} 0%, 
                  ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <CreditCardIcon 
                  color="primary" 
                  sx={{ fontSize: isMobile ? 40 : 48, mb: 1 }} 
                />
                <Tooltip 
                  title="Maximum amount of spending power you can use for BlitzPay advances" 
                  arrow 
                  placement="top"
                >
                  <Typography 
                    variant={isMobile ? "h4" : "h3"} 
                    color="primary.main" 
                    fontWeight={800}
                    sx={{ cursor: 'help' }}
                  >
                    ${effectiveLimit.toFixed(2)}
                  </Typography>
                </Tooltip>
                <Typography 
                  variant={isMobile ? "caption" : "body2"} 
                  color="text.secondary" 
                  fontWeight={500}
                >
                  {isTeamSource ? 'Team Spending Limit' : 'Company Spending Limit'}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Utilization Bar */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Tooltip title="Percentage of your spending limit currently in use" arrow>
                    <Typography 
                      variant={isMobile ? "caption" : "body2"} 
                      color="text.secondary" 
                      fontWeight={600}
                      sx={{ cursor: 'help' }}
                    >
                      Utilization
                    </Typography>
                  </Tooltip>
                  <Typography 
                    variant={isMobile ? "caption" : "body2"} 
                    fontWeight={700}
                  >
                    {utilizationPercent.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(utilizationPercent, 100)}
                  color={getUtilizationColor(utilizationPercent)}
                  sx={{ 
                    height: isMobile ? 10 : 12, 
                    borderRadius: 6,
                    backgroundColor: alpha(theme.palette.grey[300], 0.3)
                  }}
                />
              </Box>

              {/* Breakdown Grid */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <Tooltip title="Amount currently reserved for active BlitzPay advances" arrow>
                    <Box sx={{ textAlign: 'center', cursor: 'help' }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        fontWeight={600}
                        sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                      >
                        Used
                      </Typography>
                      <Typography 
                        variant={isMobile ? "subtitle1" : "h6"} 
                        fontWeight={700} 
                        color="warning.main"
                      >
                        ${teamUsed.toFixed(2)}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
                <Grid item xs={6}>
                  <Tooltip title="Amount available for new BlitzPay advances" arrow>
                    <Box sx={{ textAlign: 'center', cursor: 'help' }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        fontWeight={600}
                        sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                      >
                        Available
                      </Typography>
                      <Typography 
                        variant={isMobile ? "subtitle1" : "h6"} 
                        fontWeight={700} 
                        color="success.main"
                      >
                        ${teamAvailable.toFixed(2)}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              </Grid>
            </Card>

            {/* Company Overview */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography 
                  variant={isMobile ? "body1" : "subtitle1"} 
                  fontWeight={700}
                >
                  Company Overview
                </Typography>
                <Tooltip title="Company-wide spending power statistics" arrow>
                  <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      p: isMobile ? 1.5 : 2, 
                      borderRadius: 2, 
                      textAlign: 'center' 
                    }}
                  >
                    <AccountBalanceIcon 
                      sx={{ 
                        color: 'info.main', 
                        fontSize: isMobile ? 28 : 32, 
                        mb: 1 
                      }} 
                    />
                    <Typography 
                      variant={isMobile ? "subtitle1" : "h6"} 
                      fontWeight={700}
                    >
                      ${spendingInfo.company_spending_limit || '0'}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                    >
                      Company Limit
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      p: isMobile ? 1.5 : 2, 
                      borderRadius: 2, 
                      textAlign: 'center' 
                    }}
                  >
                    <AttachMoneyIcon 
                      sx={{ 
                        color: 'success.main', 
                        fontSize: isMobile ? 28 : 32, 
                        mb: 1 
                      }} 
                    />
                    <Typography 
                      variant={isMobile ? "subtitle1" : "h6"} 
                      fontWeight={700}
                    >
                      ${spendingInfo.total_available_funds || '0'}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                    >
                      Total Available
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Team Memberships */}
            {hasTeams && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography 
                    variant={isMobile ? "body1" : "subtitle1"} 
                    fontWeight={700}
                  >
                    Your Team Memberships
                  </Typography>
                  <Tooltip title="Teams you belong to and their spending power allocations" arrow>
                    <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                <Stack spacing={2}>
                  {spendingInfo.teams.map(team => {
                    const teamAllocated = parseFloat(team.allocated_spending_power || 0);
                    const teamUsed = parseFloat(team.used_spending_power || 0);
                    const teamUtilization = teamAllocated > 0 ? (teamUsed / teamAllocated) * 100 : 0;
                    const isPrimary = team.id === spendingInfo.primary_team_id;

                    return (
                      <Card 
                        key={team.id} 
                        variant="outlined" 
                        sx={{ 
                          p: isMobile ? 1.5 : 2, 
                          borderRadius: 2,
                          border: isPrimary ? `2px solid ${theme.palette.primary.main}` : undefined,
                          bgcolor: isPrimary ? alpha(theme.palette.primary.main, 0.02) : undefined,
                        }}
                      >
                        <Box sx={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "flex-start",
                          mb: 2,
                          gap: 2,
                          flexDirection: isMobile ? 'column' : 'row',
                        }}>
                          <Box sx={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: 1.5, 
                            flex: 1, 
                            minWidth: 0,
                            width: isMobile ? '100%' : 'auto',
                          }}>
                            <Avatar 
                              sx={{ 
                                bgcolor: alpha(theme.palette.primary.main, 0.1), 
                                width: isMobile ? 36 : 40, 
                                height: isMobile ? 36 : 40 
                              }}
                            >
                              <GroupIcon 
                                color="primary" 
                                sx={{ fontSize: isMobile ? 18 : 20 }} 
                              />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1, 
                                mb: 0.5,
                                flexWrap: 'wrap'
                              }}>
                                <Typography 
                                  variant={isMobile ? "body2" : "body1"} 
                                  fontWeight={700}
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {team.name}
                                </Typography>
                                {isPrimary && (
                                  <Tooltip title="Your primary team - transactions default to this team" arrow>
                                    <Chip 
                                      label="Primary" 
                                      size="small" 
                                      color="primary"
                                      sx={{ 
                                        height: isMobile ? 18 : 20, 
                                        fontSize: isMobile ? '0.65rem' : '0.7rem', 
                                        fontWeight: 700 
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                              >
                                {teamUtilization.toFixed(1)}% utilized
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ 
                            textAlign: isMobile ? "left" : "right", 
                            flexShrink: 0,
                            width: isMobile ? '100%' : 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: isMobile ? 'space-between' : 'flex-end',
                            gap: 2,
                          }}>
                            <Tooltip title="Team's total allocated spending power" arrow>
                              <Typography 
                                variant={isMobile ? "h6" : "h6"} 
                                fontWeight={700} 
                                color="primary.main"
                                sx={{ cursor: 'help' }}
                              >
                                ${teamAllocated.toFixed(0)}
                              </Typography>
                            </Tooltip>
                            <Chip 
                              label={team.spending_power_status || 'active'} 
                              size="small"
                              color={team.spending_power_status === 'active' ? 'success' : 'warning'}
                              sx={{ 
                                height: isMobile ? 18 : 20, 
                                fontSize: isMobile ? '0.65rem' : '0.7rem' 
                              }}
                            />
                          </Box>
                        </Box>
                        
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(teamUtilization, 100)}
                          color={getUtilizationColor(teamUtilization)}
                          sx={{ 
                            height: isMobile ? 6 : 8, 
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.grey[300], 0.3)
                          }}
                        />
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* Rate Information */}
            <Card 
              sx={{ 
                p: isMobile ? 2 : 2.5, 
                bgcolor: alpha(theme.palette.success.main, 0.08),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 1.5,
                flexDirection: isMobile ? 'column' : 'row',
                textAlign: isMobile ? 'center' : 'left',
              }}>
                <Box
                  sx={{
                    width: isMobile ? 36 : 40,
                    height: isMobile ? 36 : 40,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.15),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <TrendingUpIcon 
                    sx={{ 
                      color: 'success.main', 
                      fontSize: isMobile ? 20 : 24 
                    }} 
                  />
                </Box>
                <Box>
                  <Typography 
                    variant={isMobile ? "caption" : "subtitle2"} 
                    fontWeight={700} 
                    color="text.secondary"
                  >
                    Effective Discount Rate
                  </Typography>
                  <Typography 
                    variant={isMobile ? "h6" : "h5"} 
                    fontWeight={800} 
                    color="success.dark"
                  >
                    {spendingInfo.effective_interest_rate || spendingInfo.company_interest_rate || '10'}%
                  </Typography>
                </Box>
              </Box>
              {isTeamSource && 
               spendingInfo.team_interest_rate && 
               spendingInfo.team_interest_rate !== spendingInfo.company_interest_rate && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    Team rate overrides company rate of {spendingInfo.company_interest_rate}%
                  </Typography>
                </Alert>
              )}
            </Card>

            {/* Funds Status */}
            {!spendingInfo.has_available_funds && (
              <Alert severity="warning" icon={<InfoIcon />}>
                <Typography 
                  variant={isMobile ? "caption" : "body2"} 
                  fontWeight={600}
                >
                  No funds currently available for spending. Contact your team leader or supervisor to request an increase.
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        
        {/* Dialog Actions */}
        <DialogActions 
          sx={{ 
            p: isMobile ? 2 : 3, 
            flexDirection: isMobile ? 'column-reverse' : 'row', 
            gap: isMobile ? 1 : 0,
            bgcolor: alpha(theme.palette.grey[100], 0.5),
            borderTop: '1px solid',
            borderColor: 'divider',
            ...(isMobile && {
              paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            }),
          }}
        >
          <Button 
            onClick={() => window.location.href = '/user'}
            variant="outlined"
            fullWidth={isMobile}
            size={isMobile ? "large" : "medium"}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: isMobile ? '0.875rem' : '1rem'
            }}
          >
            Manage Teams
          </Button>
          <Button 
            onClick={onClose} 
            variant="contained" 
            fullWidth={isMobile}
            size={isMobile ? "large" : "medium"}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: isMobile ? '0.875rem' : '1rem'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Desktop View - Full Screen Overlay
  return (
    <Fade in={open} timeout={300}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'background.default',
          zIndex: theme.zIndex.modal + 1,
          display: open ? 'flex' : 'none',
          flexDirection: 'column',
        }}
      >
        {/* Header Bar */}
        <Box
          sx={{
            height: 80,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 4,
            bgcolor: 'background.paper',
            backdropFilter: 'blur(20px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                background: isTeamSource 
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isTeamSource ? (
                <GroupIcon sx={{ color: 'white', fontSize: 28 }} />
              ) : (
                <BusinessIcon sx={{ color: 'white', fontSize: 28 }} />
              )}
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Spending Power Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isTeamSource 
                  ? `${spendingInfo.primary_team_name} • Team allocation`
                  : `${spendingInfo.company_name} • Company-wide`}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {rightPanelTab === 0 && (
              <Tooltip title="Refresh transaction history" arrow>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetchHistory()}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Refresh
                </Button>
              </Tooltip>
            )}
            <IconButton 
              onClick={onClose}
              sx={{
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Main Content - Two Column Layout */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left Column - Primary Info */}
          <Box
            sx={{
              width: 420,
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
              {/* Spending Source Alert */}
              <Alert 
                severity={isTeamSource ? "info" : "success"} 
                icon={<InfoIcon />}
                sx={{ borderRadius: 2, mb: 4 }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {isTeamSource 
                    ? `You're using team-allocated spending power`
                    : `You're using company-wide spending power`}
                </Typography>
              </Alert>

              {/* Primary Spending Power Card */}
              <Paper
                elevation={0}
                sx={{ 
                  p: 4, 
                  mb: 4,
                  borderRadius: 4,
                  background: `linear-gradient(135deg, 
                    ${alpha(theme.palette.primary.main, 0.1)} 0%, 
                    ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '100%',
                    height: '100%',
                    background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    pointerEvents: 'none',
                  },
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box sx={{ textAlign: "center", mb: 3 }}>
                    <CreditCardIcon 
                      color="primary" 
                      sx={{ fontSize: 56, mb: 2 }} 
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '1px' }}>
                      {isTeamSource ? 'TEAM SPENDING LIMIT' : 'COMPANY SPENDING LIMIT'}
                    </Typography>
                    <Tooltip title="Maximum amount of spending power available for BlitzPay advances" arrow placement="right">
                      <Typography 
                        variant="h2" 
                        color="primary.main" 
                        sx={{ fontWeight: 800, mt: 1, fontSize: '3.5rem', cursor: 'help' }}
                      >
                        ${effectiveLimit.toFixed(2)}
                      </Typography>
                    </Tooltip>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Utilization */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                      <Tooltip title="Percentage of spending limit currently in use for active BlitzPay advances" arrow>
                        <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ cursor: 'help' }}>
                          Utilization
                        </Typography>
                      </Tooltip>
                      <Typography variant="h6" fontWeight={700}>
                        {utilizationPercent.toFixed(1)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(utilizationPercent, 100)}
                      color={getUtilizationColor(utilizationPercent)}
                      sx={{ 
                        height: 14, 
                        borderRadius: 7,
                        backgroundColor: alpha(theme.palette.grey[300], 0.3)
                      }}
                    />
                  </Box>

                  {/* Breakdown */}
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Tooltip title="Amount currently reserved for active BlitzPay advances" arrow>
                        <Paper sx={{ p: 2.5, textAlign: 'center', borderRadius: 2, cursor: 'help' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Used
                          </Typography>
                          <Typography variant="h5" fontWeight={700} color="warning.main" sx={{ mt: 0.5 }}>
                            ${teamUsed.toFixed(2)}
                          </Typography>
                        </Paper>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={6}>
                      <Tooltip title="Amount available for new BlitzPay advances" arrow>
                        <Paper sx={{ p: 2.5, textAlign: 'center', borderRadius: 2, cursor: 'help' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Available
                          </Typography>
                          <Typography variant="h5" fontWeight={700} color="success.main" sx={{ mt: 0.5 }}>
                            ${teamAvailable.toFixed(2)}
                          </Typography>
                        </Paper>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>

              {/* Company Overview */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Company Overview
                  </Typography>
                  <Tooltip title="Company-wide spending power statistics across all users and teams" arrow>
                    <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Paper 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 2.5, 
                        textAlign: 'center',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: 'info.lighter',
                          color: 'info.main',
                          width: 48,
                          height: 48,
                          margin: '0 auto',
                          mb: 1.5,
                        }}
                      >
                        <AccountBalanceIcon />
                      </Avatar>
                      <Typography variant="h6" fontWeight={700}>
                        ${spendingInfo.company_spending_limit || '0'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Company Limit
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 2.5, 
                        textAlign: 'center',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: 'success.lighter',
                          color: 'success.main',
                          width: 48,
                          height: 48,
                          margin: '0 auto',
                          mb: 1.5,
                        }}
                      >
                        <AttachMoneyIcon />
                      </Avatar>
                      <Typography variant="h6" fontWeight={700}>
                        ${spendingInfo.total_available_funds || '0'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Total Available
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Rate Information */}
              <Paper 
                sx={{ 
                  p: 3, 
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                  borderRadius: 2.5,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.15),
                      color: 'success.main',
                      width: 48,
                      height: 48,
                    }}
                  >
                    <TrendingUpIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                      Effective Discount Rate
                    </Typography>
                    <Typography variant="h4" fontWeight={800} color="success.dark">
                      {spendingInfo.effective_interest_rate || spendingInfo.company_interest_rate || '10'}%
                    </Typography>
                  </Box>
                </Box>
                {isTeamSource && 
                 spendingInfo.team_interest_rate && 
                 spendingInfo.team_interest_rate !== spendingInfo.company_interest_rate && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                      Team rate overrides company rate of {spendingInfo.company_interest_rate}%
                    </Typography>
                  </Alert>
                )}
              </Paper>

              {/* Funds Status */}
              {!spendingInfo.has_available_funds && (
                <Alert severity="warning" icon={<InfoIcon />} sx={{ mt: 3 }}>
                  <Typography variant="body2" fontWeight={600}>
                    No funds currently available for spending. Contact your team leader or supervisor to request an increase.
                  </Typography>
                </Alert>
              )}

              {/* Action Button */}
              <Button
                variant="contained"
                size="large"
                startIcon={<SettingsIcon />}
                onClick={() => window.location.href = '/user'}
                fullWidth
                sx={{
                  mt: 4,
                  py: 2,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                }}
              >
                Manage Teams
              </Button>
            </Box>
          </Box>

          {/* Right Column - Transaction History & Teams */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Tab Navigation */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 4, bgcolor: 'background.paper' }}>
              <Tabs 
                value={rightPanelTab} 
                onChange={(e, newValue) => setRightPanelTab(newValue)}
                sx={{
                  '& .MuiTab-root': {
                    minHeight: 64,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    px: 3,
                  },
                }}
              >
                <Tab 
                  icon={<ReceiptIcon />} 
                  iconPosition="start" 
                  label="Transaction History" 
                />
                <Tab 
                  icon={<GroupIcon />} 
                  iconPosition="start" 
                  label={`Team Memberships ${hasTeams ? `(${spendingInfo.teams.length})` : ''}`}
                />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
              {rightPanelTab === 1 ? (
                <>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                      Your Team Memberships
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Teams you're part of and their spending allocations
                    </Typography>
                  </Box>
                  <TeamMembershipsContent />
                </>
              ) : (
                <>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                      Transaction History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Recent spending power transactions and allocations
                    </Typography>
                  </Box>
                  <TransactionHistoryContent />
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default SpendingDetailsModal;